/**
 * HTTP 请求拦截器和配置
 */

import axios from 'axios';
import type { InternalAxiosRequestConfig, AxiosResponse, AxiosError, AxiosInstance } from 'axios';
import type { RequestOptions, ResponseData } from '../types/common';
import { message } from 'antd';

// 创建 axios 实例
const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 添加 token
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 添加时间戳防止缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ResponseData>) => {
    const res = response.data;

    // 如果返回的状态码不是 200，则认为是错误
    if (res.code !== 200) {
      // 错误处理
      console.error('业务错误:', res.message || '请求失败');

      // 401: 未授权
      if (res.code === 401) {
        // 清除 token 并跳转到登录页
        localStorage.removeItem('token');
        window.location.href = '/login';
      }

      // 显示错误提示
      message.error(res.message || '请求失败');

      return Promise.reject(new Error(res.message || '请求失败'));
    }

    // 返回数据层，而不是整个响应对象
    return res.data;
  },
  (error: AxiosError) => {
    console.error('响应错误:', error.message);

    // 可以在这里处理特定的错误
    if (error.response) {
      switch (error.response.status) {
        case 401:
          console.error('未授权，请重新登录');
          localStorage.removeItem('token');
          window.location.href = '/login';
          break;
        case 403:
          // 权限不足，从响应中获取错误信息
          const errorData = error.response.data as ResponseData;
          const msg = errorData?.message || '没有权限执行此操作';
          console.error('拒绝访问:', msg);
          message.error(msg);
          break;
        case 404:
          console.error('请求资源不存在');
          message.error('请求资源不存在');
          break;
        case 500:
          console.error('服务器错误');
          message.error('服务器错误');
          break;
        default:
          console.error(`错误 ${error.response.status}`);
          message.error(`请求失败，状态码：${error.response.status}`);
      }
    } else if (error.request) {
      console.error('网络错误，请检查网络连接');
      message.error('网络错误，请检查网络连接');
    }

    return Promise.reject(error);
  }
);

/**
 * HTTP 请求方法封装
 */
export const http = {
  /**
   * GET 请求
   */
  get<T = any>(url: string, params?: object, options?: RequestOptions): Promise<T> {
    return request({
      url,
      method: 'get',
      params,
      ...options,
    });
  },

  /**
   * POST 请求
   */
  post<T = any>(url: string, data?: object, options?: RequestOptions): Promise<T> {
    return request({
      url,
      method: 'post',
      data,
      ...options,
    });
  },

  /**
   * PUT 请求
   */
  put<T = any>(url: string, data?: object, options?: RequestOptions): Promise<T> {
    return request({
      url,
      method: 'put',
      data,
      ...options,
    });
  },

  /**
   * DELETE 请求
   */
  delete<T = any>(url: string, params?: object, options?: RequestOptions): Promise<T> {
    return request({
      url,
      method: 'delete',
      params,
      ...options,
    });
  },

  /**
   * 上传文件
   */
  upload<T = any>(url: string, file: File, options?: RequestOptions): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    return request({
      url,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...options,
    });
  },
};

export default request;
